'use client'

import { useState, useEffect } from 'react'
import Link from 'next/link'
import { ArrowRight, Mail, CheckCircle, AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { analytics } from '@/lib/analytics'

interface NewsArticle {
  slug: string
  title: string
  published_at: string | null
}

export default function HomePage() {
  const [email, setEmail] = useState('')
  const [subscribed, setSubscribed] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [latestArticles, setLatestArticles] = useState<NewsArticle[] | null>(null)

  useEffect(() => {
    fetch('/api/news?limit=3')
      .then(r => r.json())
      .then(data => setLatestArticles(data.articles ?? []))
      .catch(() => setLatestArticles([]))
  }, [])

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email) return
    setLoading(true)
    setError('')
    try {
      const res = await fetch('/api/newsletter/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, interests: ['general'] }),
      })
      if (!res.ok) {
        const data = await res.json()
        setError(data.error || 'Something went wrong. Please try again.')
      } else {
        setSubscribed(true)
        analytics.newsletterSubscribed({ source: 'homepage' })
        setEmail('')
        setTimeout(() => setSubscribed(false), 4000)
      }
    } catch {
      setError('Something went wrong. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="bg-background text-charcoal">

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-background py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto animate-fade-in">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              AI is powerful.
              <br />
              <span className="text-primary">The instructions suck.</span>
            </h1>
            <p className="text-lg sm:text-xl text-warm-gray mb-8 leading-relaxed">
              You're not behind on AI. You just need someone to explain it in plain English, without the jargon.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link href="/signup" onClick={() => analytics.ctaClicked({ location: 'hero', cta: 'signup' })}>
                <Button className="bg-primary hover:bg-primary-700 text-white px-8 py-6 text-lg">
                  Get Help — Free
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Button variant="outline" className="border-warm-gray text-charcoal hover:bg-primary/5 px-8 py-6 text-lg" asChild>
                <a href="#how-it-works" onClick={() => analytics.ctaClicked({ location: 'hero', cta: 'how_it_works' })}>See how it works</a>
              </Button>
            </div>
            <p className="text-sm sm:text-base text-warm-gray mt-4">No credit card required &middot; 5 free sessions included</p>
          </div>

          {/* Hero Mock Chat UI */}
          <div className="mt-16">
            <div className="bg-white rounded-xl border border-gray-200 shadow-md max-w-lg mx-auto overflow-hidden">
              {/* Browser chrome */}
              <div className="flex items-center gap-1.5 px-4 py-3 bg-gray-50 border-b border-gray-200">
                <div className="w-3 h-3 rounded-full bg-red-400" />
                <div className="w-3 h-3 rounded-full bg-yellow-400" />
                <div className="w-3 h-3 rounded-full bg-green-400" />
                <span className="ml-2 text-xs text-gray-400 font-medium select-none">TheNormal.AI</span>
              </div>
              {/* Chat messages */}
              <div className="p-6 space-y-4">
                {/* User message */}
                <div className="flex justify-end">
                  <div className="bg-primary text-white px-4 py-3 rounded-lg text-sm leading-relaxed max-w-[85%]">
                    How do I write a good prompt for ChatGPT?
                  </div>
                </div>
                {/* AI response */}
                <div className="flex justify-start">
                  <div className="bg-gray-50 border border-gray-200 px-4 py-3 rounded-lg text-sm leading-relaxed text-charcoal max-w-[85%]">
                    Think of it like giving directions — the more specific, the better. Try:{' '}
                    <span className="text-primary font-medium">&ldquo;Explain [topic] in 3 sentences for someone with no background in it.&rdquo;</span>
                    {' '}Clear role, clear goal, clear format. You&rsquo;ve got this.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-20 sm:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">How it works</h2>
            <p className="text-warm-gray text-lg">Three simple steps to get unstuck</p>
          </div>

          <div className="grid md:grid-cols-3 gap-4 md:gap-8">
            {/* Step 1 */}
            <div className="p-6 sm:p-8 bg-background rounded-lg border border-primary/10">
              <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-primary/10 text-primary font-bold text-lg mb-4">
                1
              </div>
              <h3 className="text-xl font-semibold mb-3">Tell us what you're working with</h3>
              <p className="text-warm-gray leading-relaxed">
                Share your task, tool, or question. No tech background needed. We understand human, not just code.
              </p>
            </div>

            {/* Step 2 */}
            <div className="p-6 sm:p-8 bg-background rounded-lg border border-primary/10">
              <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-primary/10 text-primary font-bold text-lg mb-4">
                2
              </div>
              <h3 className="text-xl font-semibold mb-3">Chat with our AI helper</h3>
              <p className="text-warm-gray leading-relaxed">
                Get real-time answers in plain English. Ask follow-ups. No confusing docs, no forum scrolling.
              </p>
            </div>

            {/* Step 3 */}
            <div className="p-6 sm:p-8 bg-background rounded-lg border border-primary/10">
              <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-primary/10 text-primary font-bold text-lg mb-4">
                3
              </div>
              <h3 className="text-xl font-semibold mb-3">Get unstuck or talk to a human</h3>
              <p className="text-warm-gray leading-relaxed">
                Most problems solved in chat. Stuck? We're bringing human experts soon—on us.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Popular Guides Section */}
      <section className="py-20 sm:py-28 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-end mb-12">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-2">Popular guides</h2>
              <p className="text-warm-gray">Learn from real people solving real problems</p>
            </div>
            <Link href="/guides" className="text-primary font-semibold hover:underline flex items-center gap-2">
              View all <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {[
              { title: 'Getting started with ChatGPT', difficulty: 'Beginner', tool: 'ChatGPT', slug: 'getting-started-chatgpt' },
              { title: 'Making great prompts', difficulty: 'Beginner', tool: 'General', slug: 'prompting-101' },
              { title: 'Using AI for content creation', difficulty: 'Intermediate', tool: 'ChatGPT', slug: 'ai-content-creators' },
              { title: 'Automating tasks with no-code tools', difficulty: 'Intermediate', tool: 'Zapier', slug: 'zapier-automation-guide' },
            ].map((guide, i) => (
              <Link href={`/guides/${guide.slug}`} key={i} className="group">
                <div className="p-6 bg-white rounded-lg border border-gray-200 hover:border-primary hover:shadow-md transition-all h-full">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-semibold px-2 py-1 bg-primary/10 text-primary rounded">
                      {guide.difficulty}
                    </span>
                    <span className="text-xs text-warm-gray">{guide.tool}</span>
                  </div>
                  <h3 className="font-semibold text-charcoal group-hover:text-primary transition-colors leading-snug">
                    {guide.title}
                  </h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* This Week in AI Section */}
      <section className="py-20 sm:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-end mb-12">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-2">This week in AI</h2>
              <p className="text-warm-gray">What's actually worth paying attention to</p>
            </div>
            <Link href="/news" className="text-primary font-semibold hover:underline flex items-center gap-2">
              Read more <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid md:grid-cols-3 gap-4 md:gap-8">
            {latestArticles === null ? (
              // Loading skeleton with shimmer animation
              Array.from({ length: 3 }).map((_, i) => (
                <div
                  key={i}
                  className="p-6 bg-gradient-to-r from-gray-100 via-gray-50 to-gray-100 rounded-lg border border-gray-200 h-full flex flex-col animate-shimmer"
                  style={{
                    backgroundSize: '200% 100%',
                  }}
                >
                  <div className="h-3 bg-gray-200 rounded w-24 mb-3 opacity-50" />
                  <div className="h-5 bg-gray-200 rounded w-full mb-2 flex-1 opacity-50" />
                  <div className="h-5 bg-gray-200 rounded w-3/4 mb-4 opacity-50" />
                  <div className="h-4 bg-gray-100 rounded w-16 opacity-50" />
                </div>
              ))
            ) : latestArticles.length === 0 ? (
              <p className="text-warm-gray col-span-3 text-center py-8">No articles yet — check back soon.</p>
            ) : (
              latestArticles.map((article, i) => (
                <Link href={`/news/${article.slug}`} key={i} className="group">
                  <div className="p-6 bg-background rounded-lg border border-gray-200 hover:border-primary transition-colors h-full flex flex-col">
                    <p className="text-sm text-warm-gray mb-2">
                      {article.published_at
                        ? new Date(article.published_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                        : ''}
                    </p>
                    <h3 className="font-semibold text-lg text-charcoal group-hover:text-primary transition-colors flex-1 mb-4">
                      {article.title}
                    </h3>
                    <span className="text-primary text-sm font-semibold flex items-center gap-1">
                      Read <ArrowRight className="w-3 h-3" />
                    </span>
                  </div>
                </Link>
              ))
            )}
          </div>
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="py-20 sm:py-28 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">What early users are saying</h2>
            <p className="text-warm-gray text-lg">Feedback from our beta community</p>
          </div>

          <div className="grid md:grid-cols-3 gap-4 md:gap-8">
            {[
              {
                quote: 'Set up my first ChatGPT email workflow in 20 minutes. Now I save 3 hours a week on customer replies.',
                author: 'Linda Torres',
                role: 'Owner, Sunrise Bakery',
                initials: 'LT',
                avatarClass: 'bg-violet-100 text-violet-700',
              },
              {
                quote: 'We figured out which AI tools actually help with contracts — without buying stuff we never used.',
                author: 'Marcus Webb',
                role: 'Operations Manager, small law firm',
                initials: 'MW',
                avatarClass: 'bg-emerald-100 text-emerald-700',
              },
              {
                quote: 'I avoided tech for years. Now I build AI lesson plans in half the time and my students love them.',
                author: 'Priya Nair',
                role: '4th Grade Teacher, public school',
                initials: 'PN',
                avatarClass: 'bg-amber-100 text-amber-700',
              },
            ].map((testimonial, i) => (
              <div key={i} className="p-6 sm:p-8 bg-white rounded-lg border border-gray-200">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, j) => (
                    <span key={j} className="text-warning">★</span>
                  ))}
                </div>
                <p className="text-charcoal mb-6 leading-relaxed italic">"{testimonial.quote}"</p>
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full ${testimonial.avatarClass} flex items-center justify-center text-sm font-semibold flex-shrink-0`}>
                    {testimonial.initials}
                  </div>
                  <div>
                    <p className="font-semibold text-charcoal">{testimonial.author}</p>
                    <p className="text-sm text-warm-gray">{testimonial.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter CTA Section */}
      <section className="py-20 sm:py-28 bg-white">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex justify-center mb-4">
            <Mail className="w-12 h-12 text-primary" />
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">Stay in the loop</h2>
          <p className="text-lg text-warm-gray mb-8">
            3 AI things that matter, every Monday. No hype, no spam—just useful.
          </p>

          <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <input
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={`w-full px-4 py-3 rounded-lg border transition-colors focus:outline-none ${
                  subscribed
                    ? 'border-green-300 bg-green-50 focus:border-green-400'
                    : error
                    ? 'border-red-300 bg-red-50 focus:border-red-400'
                    : 'border-gray-300 focus:border-primary'
                }`}
                required
                disabled={loading || subscribed}
              />
              {subscribed && (
                <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-green-600" />
              )}
            </div>
            <Button
              type="submit"
              className="bg-primary hover:bg-primary-700 text-white px-6 py-3"
              disabled={loading || subscribed}
            >
              {loading ? 'Subscribing...' : subscribed ? 'Subscribed!' : 'Subscribe'}
            </Button>
          </form>

          {subscribed && (
            <p className="text-green-700 mt-4 font-semibold flex items-center justify-center gap-2 animate-fade-in">
              <CheckCircle className="w-4 h-4" />
              Thanks! Check your inbox.
            </p>
          )}
          {error && (
            <p className="text-red-600 mt-4 text-sm flex items-center justify-center gap-2 animate-fade-in">
              <AlertCircle className="w-4 h-4" />
              {error}
            </p>
          )}
        </div>
      </section>

    </div>
  )
}
